import React, { useState, useEffect } from 'react';
import { tickerAPI } from '../../services/api';

const TICKER_TYPES = [
  { value: 1, label: '📢 Info', color: 'bg-slate-100 text-slate-700' },
  { value: 2, label: '⚽ Goal', color: 'bg-green-100 text-green-700' },
  { value: 3, label: '🟨 Yellow Card', color: 'bg-yellow-100 text-yellow-700' },
  { value: 4, label: '🟥 Red Card', color: 'bg-red-100 text-red-700' },
  { value: 5, label: '🔄 Substitution', color: 'bg-blue-100 text-blue-700' },
  { value: 6, label: '⏱️ Half Time', color: 'bg-purple-100 text-purple-700' },
  { value: 7, label: '🏁 Full Time', color: 'bg-indigo-100 text-indigo-700' },
];

const typeStyle = (type) => TICKER_TYPES.find(t => t.value === type)?.color || 'bg-slate-100 text-slate-700';
const typeLabel = (type) => TICKER_TYPES.find(t => t.value === type)?.label || '📢 Info';

const GameTicker = ({ game, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [minute, setMinute] = useState('');
  const [type, setType] = useState(1);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchTicker();
  }, [game._id]);

  const fetchTicker = async () => {
    try {
      const res = await tickerAPI.getByGame(game._id);
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Could not load ticker entries.');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      await tickerAPI.create({
        gid: game._id,
        type: parseInt(type),
        misc: { text: text.trim(), minute: parseInt(minute) || 0 },
        published: true,
      });
      setText('');
      setMinute('');
      setType(1);
      fetchTicker();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to post ticker entry.');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await tickerAPI.delete(id);
      fetchTicker();
    } catch (err) {
      setError('Failed to delete entry.');
    }
  };

  const handleTogglePublish = async (entry) => {
    try {
      await tickerAPI.update(entry._id, { published: !entry.published });
      fetchTicker();
    } catch (err) {
      setError('Failed to update entry.');
    }
  };

  const sorted = [...entries].sort((a, b) => {
    const mA = a.misc?.minute ?? 0;
    const mB = b.misc?.minute ?? 0;
    return mB - mA;
  });

  return (
    <dialog open className="modal">
      <div className="modal-box w-11/12 max-w-2xl">
        <h3 className="font-bold text-lg">
          Live Ticker — {game.hid?.name} vs {game.aid?.name}
        </h3>

        {error && (
          <div className="alert alert-error mt-2 text-sm py-2">{error}</div>
        )}

        {/* Post form */}
        <form onSubmit={handlePost} className="mt-4 space-y-3">
          <div className="flex gap-2">
            <div className="form-control w-24 flex-shrink-0">
              <label className="label"><span className="label-text">Minute</span></label>
              <input
                type="number" min="0" max="120"
                className="input input-bordered text-center"
                value={minute}
                onChange={e => setMinute(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-control flex-shrink-0 w-44">
              <label className="label"><span className="label-text">Type</span></label>
              <select className="select select-bordered" value={type} onChange={e => setType(e.target.value)}>
                {TICKER_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="form-control flex-1">
              <label className="label"><span className="label-text">Message *</span></label>
              <input
                type="text"
                className="input input-bordered"
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Ticker message…"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={posting || !text.trim()}
          >
            {posting ? <span className="loading loading-spinner loading-xs"></span> : '📡 Post Entry'}
          </button>
        </form>

        <div className="divider text-xs text-gray-400">Entries ({entries.length})</div>

        {loading ? (
          <div className="flex justify-center py-6">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <div className="text-4xl mb-2">📡</div>
            <p className="text-sm">No ticker entries yet. Post the first one above.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {sorted.map(entry => (
              <div
                key={entry._id}
                className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                  entry.published ? 'border border-gray-100' : 'border border-dashed border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${typeStyle(entry.type)}`}>
                    {entry.misc?.minute != null ? `${entry.misc.minute}'` : '—'}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${typeStyle(entry.type)}`}>
                    {typeLabel(entry.type)}
                  </span>
                  <span className="text-sm text-gray-800">{entry.misc?.text || '—'}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    className={`btn btn-xs ${entry.published ? 'btn-ghost text-green-600' : 'btn-ghost text-gray-400'}`}
                    onClick={() => handleTogglePublish(entry)}
                    title={entry.published ? 'Published – click to unpublish' : 'Unpublished – click to publish'}
                  >
                    {entry.published ? '✓' : '○'}
                  </button>
                  <button
                    className="btn btn-xs btn-ghost text-red-500 btn-square"
                    onClick={() => handleDelete(entry._id)}
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="modal-action">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </dialog>
  );
};

export default GameTicker;
